import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import ShellSaac, { useVoltar } from '../../../components/shell/ShellSaac';
import {
  Botao,
  Card,
  Chip,
  EmptyState,
  ErrorState,
  Nota,
  PageHeader,
  SearchInput,
  Skeleton,
  Tabs,
  idDaAba,
  idDoPainel,
} from '../../../ui';

import {
  AcompanhamentoDeOficina,
  ComentarioDaOficina,
  VeiculoNaOficina,
  fetchAcompanhamentoDeOficina,
} from '../../../api/oficina/oficinaApi';
import { extrairMensagemErro } from '../../../utils/apiError';
import { formatarDataCurta, formatarDataHora } from '../../../utils/data';
import { decodeJwtPayload } from '../../../utils/jwt';
import { RootState } from '../../../store/store';
import { HOME } from '../../home/routes';

/**
 * `/acompanhamento-oficina` — **Acompanhamento de Oficina** (at-53, 3ª entrega;
 * reescrita na at-66, frente D).
 *
 * Tela própria para os veículos do consultor que estão na oficina — antes isso vivia espalhado
 * pela carteira (chip na linha, bloco no modal, seção de novidades no topo). O Victor recusou
 * aquele desenho: *"Nada de oficina na carteira"*. A carteira voltou a ser sobre a carteira; o
 * andamento da oficina é assunto de tela, com espaço para o que o consultor precisa dizer ao
 * associado que ligou.
 *
 * ## Três regras que não são preferência
 *
 * 1. 🔴 **Falha de carga NUNCA vira "nenhum veículo na oficina".** É a afirmação que o consultor
 *    repassa ao associado. `fetchAcompanhamentoDeOficina` lança de propósito (ao contrário de
 *    `fetchNovidades`), a lista é **descartada** e o que aparece é o `ErrorState`.
 * 2. 🔴 **"Integração indisponível" e "nenhum veículo" são coisas DIFERENTES**, e a tela as
 *    distingue: `configurado: false` significa que este ambiente não fala com a oficina, e o que
 *    aparece é o último estado conhecido do espelho (que pode ser nenhum).
 * 3. ⚠️ **A previsão é o prazo TOTAL da etapa, não os dias que faltam.** Todo texto sobre ela diz
 *    isso por extenso — a própria tela da oficina avisa, e ler como "faltam N dias" faz o
 *    consultor prometer data errada ao associado.
 *
 * A previsão de ENTREGA da oficina não é exibida em lugar nenhum (decisão do Victor, 07/09/2026):
 * é previsão do time de lá, e expô-la ao consultor viraria cobrança sobre eles.
 *
 * ## O que mudou de comportamento na at-66
 *
 *   1. 🐛 **A regra 2 acima era violada no vazio.** Com a integração desligada, a tela dizia
 *      *"Nenhum veículo seu está em atendimento na oficina"* — uma **afirmação sobre a oficina**
 *      feita por um ambiente que não fala com ela. Agora esse caso tem texto próprio.
 *   2. 🐛 **O carregamento não esconde mais abas, busca e legenda.** Era `FullScreenLoader`, um
 *      overlay de tela cheia — disparado também no clique em "Atualizar", que cobria uma lista
 *      que já estava na tela. Virou `Skeleton` na primeira carga e estado do botão no resto.
 *   3. 🐛 **A falha era dita duas vezes** (`toast.error` **e** faixa vermelha, com o mesmo texto).
 *      Como a faixa fica e o toast passa, o toast era ruído. Ficou só o `ErrorState`.
 *   4. 🔴 **"Não foi possível falar com a oficina" deixou de ser VERMELHO.** Ali há conteúdo na
 *      tela e ele é legítimo — só pode estar velho. Vermelho ficou reservado ao `ErrorState`
 *      ("a tela não carregou"); a ressalva virou `Nota variante="atencao"` (âmbar).
 *   5. 🔴 **O que só existia em `title` virou texto** (at-54: `title` não existe no celular) — em
 *      especial a explicação de "Atendimento anterior", que o próprio código chamava de *"não é
 *      enfeite"* e que só aparecia ao passar o mouse.
 *   6. 🐛 **O contador das abas não afirma mais zero** enquanto não há dados: com a carga falhando,
 *      os rótulos ficavam "Na oficina (0)" e "Entregues (0)" — um zero sobre o que a tela acabara
 *      de dizer que não sabe.
 *   7. **Abas com teclado** (setas, Home/End), pelo `Tabs` do kit.
 *   8. O chip de etapa era **azul** (cor fora dos tokens) para não ser lido como cobrança; virou
 *      `Chip neutro`, que é exatamente esse significado no design system.
 *
 * ⚠️ **Preservado:** a reconsulta automática única do cold start, os textos que vêm prontos do
 * backend (`consulta.mensagem`), a busca local sobre a aba ativa, o carimbo "última consulta que
 * deu certo", a declaração do corte dos registros e a marca "Atendimento anterior".
 */

type Aba = 'NA_OFICINA' | 'ENTREGUES';

type EstadoLista = 'carregando' | 'ok' | 'erro';

/**
 * Como a carga foi disparada — é o que decide **o que a tela esconde** enquanto espera.
 *
 * `inicial` não tem o que preservar (não há lista); `atualizar` tem, e cobri-la com um loader
 * deixaria o consultor sem saber o que esperar; `silenciosa` é a reconsulta do cold start, que
 * acontece sem ninguém ter pedido e não pode piscar nada na tela.
 */
type ModoDeCarga = 'inicial' | 'atualizar' | 'silenciosa';

/**
 * Quanto esperar antes de reconsultar quando a oficina ficou `EM_ANDAMENTO`.
 *
 * ⚠️ O número mira o **cold start do Cloud Run**: a oficina escala a zero e o primeiro acesso depois
 * de um tempo ociosa paga o boot (Nest + dois clients Prisma, ~6s medidos). O read-through do
 * backend espera só 2,5s de propósito — quem está do outro lado é uma pessoa com a tela aberta —,
 * então a segunda leitura é o que fecha o caso, sem transformar toda abertura numa espera longa.
 */
const ESPERA_DA_RECONSULTA_MS = 7_000;

/**
 * A ressalva que acompanha toda falha de carga — a regra 1 do cabeçalho dita ao
 * usuário, e não só ao código.
 */
const RESSALVA_DA_FALHA =
  'Esta tela não afirma que você não tem veículo na oficina; ela apenas não conseguiu perguntar.';

const AcompanhamentoOficinaPage: React.FC = () => {
  const voltar = useVoltar(HOME());

  const accessToken = useSelector((state: RootState) => state.auth.accessToken);

  // Primeiro nome ao lado do título, como em `/carteira` e `/adesoes`. Vem do
  // JWT — o `authSlice` não guarda o nome.
  const primeiroNome = useMemo(() => {
    if (!accessToken) return null;
    const payload = decodeJwtPayload(accessToken) as { nome?: string } | null;
    return payload?.nome?.trim().split(/\s+/)[0] ?? null;
  }, [accessToken]);

  const [dados, setDados] = useState<AcompanhamentoDeOficina | null>(null);
  const [estado, setEstado] = useState<EstadoLista>('carregando');
  const [atualizando, setAtualizando] = useState(false);
  const [erro, setErro] = useState('');
  const [aba, setAba] = useState<Aba>('NA_OFICINA');
  const [busca, setBusca] = useState('');

  /**
   * Só para repor o texto no `SearchInput` quando a TELA limpa a busca por conta
   * própria (ele guarda o próprio estado e só relê `valorInicial` quando a chave
   * muda). Mesmo mecanismo da `/carteira` e do painel `/vistoria`.
   */
  const [chaveBusca, setChaveBusca] = useState(0);

  /** Uma reconsulta por espera, para o cold start não virar polling infinito. */
  const jaReconsultou = useRef(false);

  const carregar = useCallback(async (modo: ModoDeCarga = 'inicial') => {
    if (modo === 'inicial') setEstado('carregando');
    if (modo === 'atualizar') setAtualizando(true);

    try {
      const resposta = await fetchAcompanhamentoDeOficina();
      setDados(resposta);
      setErro('');
      setEstado('ok');
    } catch (e) {
      // Regra 1 do cabeçalho: o estado anterior é descartado junto com a certeza —
      // dado velho passando por atual é o defeito que o carimbo de data existe
      // para evitar (lição da at-53).
      setDados(null);
      /*
       * ⚠️ Guarda só o DETALHE técnico (fallback vazio), porque a frase em
       * linguagem de usuário é fixa no `ErrorState`. Antes o estado recebia a
       * mensagem com fallback e ia inteiro para a tela — e como
       * `extrairMensagemErro` devolve `error.message` quando existe, o que o
       * consultor lia era "Network Error", não a frase escrita para ele. O
       * fallback praticamente nunca era alcançado.
       */
      setErro(extrairMensagemErro(e, ''));
      setEstado('erro');
    } finally {
      if (modo === 'atualizar') setAtualizando(false);
    }
  }, []);

  useEffect(() => {
    void carregar('inicial');
  }, [carregar]);

  /**
   * A oficina acordou depois do orçamento: a sincronização terminou em segundo plano, então uma
   * segunda leitura mostra o dado novo. É o que faz o cold start ser invisível para o consultor,
   * em vez de exigir que ele clique "Atualizar" de novo sem saber por quê.
   *
   * ⚠️ **Uma só, nunca um laço** — polling contra um serviço que hiberna é o que o
   * `--min-instances` resolve, não a tela (item 59 do backlog).
   */
  useEffect(() => {
    if (dados?.consulta?.estado !== 'EM_ANDAMENTO' || jaReconsultou.current) return undefined;

    jaReconsultou.current = true;
    const timer = setTimeout(() => void carregar('silenciosa'), ESPERA_DA_RECONSULTA_MS);
    return () => clearTimeout(timer);
  }, [dados, carregar]);

  const filtrada = useMemo(() => {
    // A lista da aba é derivada AQUI dentro de propósito: calculada fora, ela
    // seria um array novo a cada render e o useMemo nunca reaproveitaria nada.
    const lista = aba === 'NA_OFICINA' ? dados?.naOficina ?? [] : dados?.entregues ?? [];

    const termo = busca.trim().toUpperCase();
    if (!termo) return lista;

    return lista.filter(
      (veiculo) =>
        veiculo.placa.toUpperCase().includes(termo) ||
        (veiculo.modelo || '').toUpperCase().includes(termo) ||
        (veiculo.associado || '').toUpperCase().includes(termo),
    );
  }, [dados, aba, busca]);

  const limparBusca = () => {
    setBusca('');
    setChaveBusca((n) => n + 1);
  };

  /*
   * ⚠️ O contador só existe quando HÁ dados. Sem isso, a carga falhando deixava
   * "Na oficina (0)" na tela — um número afirmando justamente o que a faixa de
   * erro ao lado acabou de dizer que não foi possível saber.
   */
  const abas = [
    {
      id: 'NA_OFICINA' as const,
      rotulo: dados ? `Na oficina (${dados.naOficina.length})` : 'Na oficina',
    },
    {
      id: 'ENTREGUES' as const,
      rotulo: dados ? `Entregues (${dados.entregues.length})` : 'Entregues',
    },
  ];

  const conteudo = () => {
    if (estado === 'carregando') return <Skeleton linhas={3} altura="tw-h-28" />;

    if (estado === 'erro') {
      return (
        <ErrorState
          mensagem="Não foi possível carregar o acompanhamento da oficina."
          detalhe={[erro, RESSALVA_DA_FALHA].filter(Boolean).join(' — ')}
          aoTentarNovamente={() => void carregar('inicial')}
        />
      );
    }

    if (filtrada.length === 0) return <Vazio aba={aba} busca={busca} dados={dados} aoLimparBusca={limparBusca} />;

    return (
      <div className="tw-flex tw-flex-col tw-gap-3">
        {filtrada.map((veiculo) => (
          <CardDoVeiculo key={veiculo.placa} veiculo={veiculo} entregue={aba === 'ENTREGUES'} />
        ))}
      </div>
    );
  };

  return (
    <ShellSaac
      cabecalho={
        /*
         * ⚠️ **"Oficina" no sobretítulo, "Acompanhamento" no título** — e não o nome inteiro do
         * menu. Na faixa do mobile o título disputa espaço com gatilho do menu, voltar, ação e
         * sino: com "Acompanhamento de Oficina" numa linha só, sobravam ~110px e o `truncate` o
         * reduzia a **"Acom..."** (visto por Victor em 10/09/2026), que não diz em que tela a
         * pessoa está. Partido em dois, o nome aparece inteiro e continua batendo com o menu.
         * É o mesmo recorte que `/vistoria/captura` usa (at-63): sobretítulo = o assunto, título
         * = a tela.
         */
        <PageHeader
          sobretitulo="Oficina"
          titulo="Acompanhamento"
          complemento={primeiroNome ?? undefined}
          aoVoltar={voltar}
          acaoDireita={
            <Botao
              variante="secundario"
              tamanho="md"
              iconeEsquerda="recarregar"
              carregando={atualizando}
              disabled={estado === 'carregando' || atualizando}
              onClick={() => void carregar('atualizar')}
              /* O rótulo some no mobile (abaixo), então o nome acessível tem de vir daqui —
                 senão o botão fica sendo só um ícone para quem usa leitor de tela. */
              aria-label="Atualizar"
            >
              {/*
                ⚠️ O texto reaparece em `lg`, que é exatamente onde o `PageHeader` troca a faixa
                laranja pela branca — as duas decisões acompanham o mesmo ponto de quebra.
                📌 Escondido, o `<span>` deixa de ser item flex, então o `gap` do botão não sobra
                como espaço morto ao lado do ícone.
              */}
              <span className="tw-hidden lg:tw-inline">Atualizar</span>
            </Botao>
          }
        />
      }
    >
      <div className="tw-min-h-full tw-bg-shell">
        <div className="tw-mx-auto tw-w-full tw-max-w-6xl md:tw-p-6">
          <div className="tw-bg-white tw-p-4 md:tw-rounded-2xl md:tw-border md:tw-border-superficie-borda md:tw-p-6 md:tw-shadow-[0_2px_12px_rgba(0,0,0,0.09)]">
            <div className="tw-flex tw-flex-col tw-gap-4">
              <Nota>
                Os veículos dos seus associados que estão em atendimento na oficina, e os entregues
                nos últimos dias. Quem informa a etapa e os registros é a oficina — esta tela mostra
                o que ela respondeu na última consulta.
              </Nota>

              {/*
                🔴 O desfecho da tentativa DESTA abertura, com texto pronto do backend.
                Sem isto, "Atualizar" pode não fazer nada e a data abaixo — a da consulta
                ANTERIOR — passa por atual. Foi o defeito de 08/09/2026, com o SOAC fora do ar.

                ⚠️ "Demorou" e "falhou" têm tons DIFERENTES de propósito: o primeiro se resolve
                sozinho na reconsulta (é o cold start, comportamento normal do ambiente) e fica
                neutro; o segundo pede atenção de quem lê, porque o dado na tela pode estar
                velho — e é `atencao`, não vermelho: vermelho é do `ErrorState`, que significa
                "não há conteúdo nenhum".
              */}
              {dados && dados.consulta && dados.consulta.estado !== 'ATUALIZADA' && (
                <Nota
                  variante={dados.consulta.estado === 'FALHOU' ? 'atencao' : 'neutra'}
                  /* `aria-live` só onde o texto APARECE com a tela aberta. A de
                     `NAO_CONFIGURADA` é estática — anunciá-la seria ruído. */
                  aria-live={dados.consulta.estado === 'NAO_CONFIGURADA' ? undefined : 'polite'}
                >
                  {dados.consulta.mensagem}
                  {dados.consulta.estado === 'EM_ANDAMENTO' &&
                    ' Esta tela se atualiza sozinha em instantes.'}
                </Nota>
              )}

              {dados?.consultadoEm && (
                /* ⚠️ "que deu certo" não é floreio: datar pela última TENTATIVA
                   transformaria a tela numa afirmação falsa (at-53). */
                <p className="tw-m-0 tw-text-xs tw-text-texto-desabilitado">
                  Última consulta à oficina que deu certo: {formatarDataHora(dados.consultadoEm)}
                </p>
              )}

              <Tabs
                idBase="oficina"
                abas={abas}
                ativa={aba}
                aoTrocar={setAba}
                rotuloAcessivel="Situação do veículo na oficina"
              />

              {/* Trocar de aba MANTÉM o termo: as duas listas vêm do mesmo dado, e
                  quem procura uma placa costuma querê-la nas duas. */}
              <SearchInput
                key={chaveBusca}
                valorInicial={busca}
                placeholder="Buscar por placa, modelo ou associado"
                rotuloAcessivel="Buscar veículo nesta aba"
                aoBuscar={setBusca}
              />

              <div
                role="tabpanel"
                id={idDoPainel('oficina')}
                aria-labelledby={idDaAba('oficina', aba)}
                aria-busy={estado === 'carregando'}
              >
                {conteudo()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </ShellSaac>
  );
};

/**
 * Os quatro vazios — e eles **não** são variações de texto do mesmo estado.
 *
 * 🔴 O caso de `configurado: false` é o que repara a regra 2 do cabeçalho: sem integração, dizer
 * "nenhum veículo seu está na oficina" é afirmar o que a tela não tem como saber. A faixa acima
 * explica o ambiente; aqui o vazio para de contradizê-la.
 */
const Vazio: React.FC<{
  aba: Aba;
  busca: string;
  dados: AcompanhamentoDeOficina | null;
  aoLimparBusca: () => void;
}> = ({ aba, busca, dados, aoLimparBusca }) => {
  if (busca.trim()) {
    return (
      <EmptyState
        icone="busca"
        titulo="Nenhum veículo com esse termo"
        descricao="A busca cobre placa, modelo e nome do associado, só nesta aba."
        acao={
          <Botao variante="secundario" onClick={aoLimparBusca}>
            Limpar a busca
          </Botao>
        }
      />
    );
  }

  if (dados && !dados.configurado) {
    return (
      <EmptyState
        icone="alerta"
        titulo="Sem informação da oficina neste ambiente"
        descricao="A integração com a oficina não está configurada aqui, então esta tela não tem como dizer se algum veículo seu está lá."
      />
    );
  }

  if (aba === 'NA_OFICINA') {
    return (
      <EmptyState
        titulo="Nenhum veículo seu está na oficina"
        descricao="Quando a oficina registrar um atendimento de um veículo da sua carteira, ele aparece aqui — e você é avisado pelo sino de notificações."
      />
    );
  }

  return (
    <EmptyState
      titulo="Nenhum veículo seu foi entregue recentemente"
      descricao="A oficina informa os atendimentos encerrados nos últimos dias."
    />
  );
};

/**
 * Um veículo, com o que o consultor precisa para responder ao associado sem ligar para a oficina:
 * a etapa, há quantos dias está lá, e o que a oficina registrou.
 *
 * 📌 É um **card**, e não uma linha de tabela com modal, porque o conteúdo é longo por natureza
 * (até 20 registros com respostas) e porque quem abre esta tela costuma estar **ao telefone com o
 * associado** — esconder os registros atrás de um clique por veículo custaria justamente no
 * momento em que a informação é pedida.
 */
const CardDoVeiculo: React.FC<{ veiculo: VeiculoNaOficina; entregue: boolean }> = ({
  veiculo,
  entregue,
}) => (
  <Card className={`tw-p-4 ${entregue ? 'tw-bg-superficie' : ''}`}>
    <div className="tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-2">
      <div className="tw-min-w-0">
        {/*
          ⚠️ Para motocicleta elétrica isto é a identificação gerada (`BIK0001`), não uma placa —
          mas a resposta desta rota **não** traz o campo `bikeEletrica`, então a tela não tem como
          rotulá-la como as outras fazem (at-54). Inferir pelo prefixo seria adivinhação; o campo
          é uma linha de backend, registrada como pendência no relatório da at-66.
        */}
        <div className="tw-font-marca tw-text-base tw-font-bold tw-tracking-wider tw-text-texto-titulo">
          {veiculo.placa}
        </div>
        <div className="tw-text-xs tw-text-texto-suave">
          {[veiculo.modelo, veiculo.associado].filter(Boolean).join(' · ') || '—'}
        </div>
      </div>

      {entregue ? (
        <Chip variante="ok">
          Entregue
          {veiculo.encerradoEm ? ` em ${formatarDataCurta(veiculo.encerradoEm)}` : ''}
        </Chip>
      ) : (
        /* ⚠️ `neutro`, não uma cor própria: a etapa é ESTADO do veículo em outro
           sistema, não pendência do consultor — e era exatamente isso que o azul
           de antes tentava dizer, com uma cor que não existe nos tokens. */
        <Chip variante="neutro">
          {veiculo.esteiraNome}
          {veiculo.diasNaOficina > 0 ? ` · ${veiculo.diasNaOficina} dia(s) na oficina` : ''}
        </Chip>
      )}
    </div>

    <div className="tw-mt-2.5 tw-flex tw-flex-wrap tw-gap-x-3.5 tw-gap-y-1 tw-text-xs tw-text-texto-suave">
      {veiculo.entradaEm && <span>Entrada: {formatarDataCurta(veiculo.entradaEm)}</span>}

      {!entregue && veiculo.etapaDesde && (
        <span>Nesta etapa desde {formatarDataCurta(veiculo.etapaDesde)}</span>
      )}

      {/*
        ⚠️ ZERO não é prazo — é etapa que ninguém estimou na oficina, e "Prazo total desta etapa:
        0 dia(s)" se lê como "era para sair no mesmo dia". A oficina trata esse caso do mesmo jeito
        (lá o veículo cai em "sem prazo definido", que nenhum alerta cobre), então a tela DIZ isso
        em vez de omitir: omitido, o consultor não saberia se o prazo existe e a tela não mostrou.
      */}
      {!entregue &&
        (veiculo.previsaoDiasNaEtapa ? (
          <span>
            {/* ⚠️ O rótulo diz o que o número é. Sem isso ele é lido como "faltam N dias" e o
                consultor promete data errada ao associado — a tela da oficina avisa o mesmo.
                Estava só no `title`, que não existe no celular (at-54). */}
            Prazo total desta etapa: {veiculo.previsaoDiasNaEtapa} dia(s){' '}
            <span className="tw-text-texto-desabilitado">
              (não é o que falta para a entrega)
            </span>
          </span>
        ) : (
          <span className="tw-text-texto-desabilitado">Prazo desta etapa: não informado</span>
        ))}

      <span className="tw-text-texto-desabilitado">
        Consultado em {formatarDataHora(veiculo.sincronizadoEm)}
      </span>
    </div>

    <RegistrosDaOficina veiculo={veiculo} />
  </Card>
);

/**
 * O que a oficina registrou sobre o veículo.
 *
 * 🛑 **NÃO APARECE HOJE** (decisão de Victor, 10/09/2026): *"aquilo são informações muito
 * operacionais para os consultores, preferimos que eles ainda não tenham acesso a esse tipo de
 * coisa"*. O backend passou a devolver `comentarios: []` e `totalDeComentarios: 0`, então este bloco
 * simplesmente **não renderiza** — a guarda de lista vazia logo abaixo já cuidava disso.
 *
 * 📌 **O código fica, e é isso que a decisão pede** ("deixe no código para caso precisemos no
 * futuro"). Ele continua compilando, type-checado e coberto por teste; o que o desliga é a constante
 * `COMENTARIOS_VISIVEIS_PARA_O_CONSULTOR` em `oficina-espelho.service.ts`. Trocá-la para `true` faz
 * tudo abaixo voltar a funcionar, sem migration e sem re-sincronizar (o espelho nunca parou de
 * guardar os registros).
 *
 * ⚠️ **Não "limpar" este trecho como código morto.** Ele não é morto — está desligado por uma linha
 * do outro lado, de propósito.
 *
 * 🔴 **A marca "atendimento anterior" não é enfeite.** Na oficina, comentário de escopo "placa"
 * atravessa atendimentos — o veículo que já esteve lá antes traz junto o que foi dito naquela vez.
 * Sem a marca, um comentário de meses atrás é lido como sendo desta entrada, e o consultor o
 * repassa ao associado como se fosse de agora. A própria tela da oficina marca isso.
 *
 * ⚠️ **A explicação dessa marca era um `title`** — ou seja, invisível no celular, que é onde o
 * consultor atende. Virou uma linha de texto, mostrada só quando há algum registro marcado.
 *
 * 🔴 **O corte é declarado.** A oficina manda no máximo 20 threads; quando há mais, a tela diz —
 * lista truncada em silêncio faria o consultor concluir que viu tudo.
 *
 * ⚠️ O bloco só existe quando HÁ registro. A ausência aqui significa "a oficina não registrou
 * nada" — e é só verdade porque a carga da tela inteira falha alto: sem isso, "nenhum registro" e
 * "não consegui perguntar" teriam a mesma aparência.
 */
const RegistrosDaOficina: React.FC<{ veiculo: VeiculoNaOficina }> = ({ veiculo }) => {
  const comentarios = veiculo.comentarios ?? [];
  if (!comentarios.length) return null;

  const total = veiculo.totalDeComentarios ?? comentarios.length;
  const ocultos = Math.max(0, total - comentarios.length);
  const temAnterior = comentarios.some((comentario) => comentario.deAtendimentoAnterior);

  return (
    <div className="tw-mt-3 tw-border-t tw-border-superficie-borda tw-pt-2.5">
      <div className="tw-text-xs tw-font-bold tw-text-texto-corpo">
        Registros da oficina ({total})
      </div>

      {temAnterior && (
        <p className="tw-m-0 tw-mt-1 tw-text-xs tw-text-texto-suave">
          Registros marcados como "Atendimento anterior" são de uma passagem anterior deste veículo
          pela oficina, não deste atendimento.
        </p>
      )}

      <div className="tw-mt-2 tw-flex tw-flex-col tw-gap-2">
        {comentarios.map((comentario) => (
          <Comentario key={comentario.id} comentario={comentario} />
        ))}
      </div>

      {ocultos > 0 && (
        <p className="tw-m-0 tw-mt-2 tw-text-xs tw-text-texto-desabilitado">
          Mostrando os {comentarios.length} registros mais recentes — há {ocultos} anterior(es) na
          oficina.
        </p>
      )}
    </div>
  );
};

const Comentario: React.FC<{ comentario: ComentarioDaOficina }> = ({ comentario }) => (
  <div className="tw-rounded-md tw-border-l-2 tw-border-superficie-borda tw-bg-superficie tw-px-2.5 tw-py-2">
    <CabecalhoDoRegistro
      autorNome={comentario.autorNome}
      criadoEm={comentario.criadoEm}
      escopoLabel={comentario.escopoLabel}
      /* 🔴 Ver o alerta do componente acima. */
      deAtendimentoAnterior={comentario.deAtendimentoAnterior}
    />

    <p className="tw-m-0 tw-whitespace-pre-wrap tw-break-words tw-text-xs tw-text-texto-corpo">
      {comentario.texto}
    </p>

    {/* A resposta fica recuada, para a conversa se ler como conversa. */}
    {comentario.respostas.map((resposta) => (
      <div
        key={resposta.id}
        className="tw-ml-3 tw-mt-2 tw-border-l-2 tw-border-superficie-borda tw-pl-2"
      >
        <CabecalhoDoRegistro autorNome={resposta.autorNome} criadoEm={resposta.criadoEm} />
        <p className="tw-m-0 tw-whitespace-pre-wrap tw-break-words tw-text-xs tw-text-texto-corpo">
          {resposta.texto}
        </p>
      </div>
    ))}
  </div>
);

/**
 * Autor, contexto e data de um registro — o mesmo para o comentário e para a resposta.
 *
 * 📌 O **nome do autor** aparece por decisão do Victor (08/09/2026): dá peso ao que o consultor
 * repassa ao associado (*"o Chefe respondeu que o serviço foi refeito"*).
 */
const CabecalhoDoRegistro: React.FC<{
  autorNome: string;
  criadoEm: string | null;
  escopoLabel?: string;
  deAtendimentoAnterior?: boolean;
}> = ({ autorNome, criadoEm, escopoLabel, deAtendimentoAnterior }) => (
  <div className="tw-mb-1 tw-flex tw-flex-wrap tw-items-center tw-gap-1.5 tw-text-[11px] tw-text-texto-suave">
    <strong className="tw-font-bold tw-text-texto-corpo">{autorNome}</strong>
    {escopoLabel && <Chip variante="neutro">{escopoLabel}</Chip>}
    {criadoEm && <span>{formatarDataHora(criadoEm)}</span>}
    {deAtendimentoAnterior && <Chip variante="pendente">Atendimento anterior</Chip>}
  </div>
);

export default AcompanhamentoOficinaPage;
